import { useRouter } from '@abyss/web/hooks/useRouter';
import { V2Card } from '@abyss/web/ui/Card/v2';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../../common/AdobeTagging/adobeModalTrackEvent';
import { Constants } from '../../../../../common/Constants';
import { getProviderTitle } from '../../../../../common/Utils';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../../store/useTypeaheadStore/typeaheadStore';
import {
  LogoNotAvailableContainer,
  virtualCareProviderCardCss,
  virtualCareProviderCardLinkCss,
  virtualCareProviderCardText,
  virtualCareSpecialityCardCss,
} from './VirtualCareProvidersBanner.styled';

interface Provider {
  providerName: string;
  logo?: string;
  link: string;
  linkType: string;
}

interface VirtualCareProviderCardProps {
  provider: Provider;
  serviceType: string;
  mobileScreen: boolean;
  mapView: boolean;
  setSelectedProviderName: (name: string) => void;
  setIsModalOpen: (open: boolean) => void;
  primaryCareVirtual: boolean;
}

export const VirtualCareProviderCard: React.FC<
  VirtualCareProviderCardProps
> = ({
  provider,
  serviceType,
  mobileScreen,
  mapView,
  setSelectedProviderName,
  setIsModalOpen,
  primaryCareVirtual = false,
}) => {
  const { t } = useTranslation();
  const { fromViewAll = false } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      fromViewAll: state.typeaheadSearchStore.fromViewAll,
    }))
  );
  const { getRouteParams } = useRouter();
  const urlParams = getRouteParams();

  const uiMessaging = useLagoon('ui-messaging')();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const chipValueFromUrl = urlParams?.chipValue || chipStore.chipValue;
  const virtualCareType =
    chipValueFromUrl === Constants.CHIPS_CATEGORIES.PRIMARY_CARE
      ? 'Virtual Primary Care'
      : 'Virtual Specialty Care';

  const handleGetCareClick = (e) => {
    e.preventDefault();
    setSelectedProviderName(provider?.providerName);
    setIsModalOpen(true);
    if (primaryCareVirtual) {
      adobeLinkTrackEvent({
        name: t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE'),
        location: `${virtualCareType} ${provider?.providerName}`,
        type: 'external',
      });
      adobeModalTrackEvent('continue to site');
    }
  };
  return (
    <V2Card
      css={virtualCareProviderCardCss(
        mobileScreen || mapView,
        primaryCareVirtual
      )}
      data-testid="virtual-care-provider-card"
      key={provider?.providerName}
    >
      <Flex justify="space-between">
        <Flex alignItems="flex-start" direction="column" justify="center">
          {provider?.logo ? (
            <img
              alt={`${provider?.providerName} logo`}
              data-testid={`virtual-care-provider-logo`}
              src={provider?.logo}
              style={{
                maxHeight: '30px',
                maxWidth: mobileScreen || mapView ? '120px' : '125px',
                marginBottom: 0,
                objectFit: 'contain' as const,
                display: 'block',
              }}
            />
          ) : (
            <LogoNotAvailableContainer
              css={virtualCareSpecialityCardCss(
                fromViewAll || mapView,
                mobileScreen,
                primaryCareVirtual
              )}
              data-testid={'logo-not-available'}
            >
              {'Logo not available'}
            </LogoNotAvailableContainer>
          )}
          <Text
            css={virtualCareProviderCardText(mobileScreen || mapView)}
            data-testid="virtual-care-provider-service-type"
          >
            {getProviderTitle(serviceType, uiMessaging, provider)}
          </Text>
        </Flex>
        <Flex alignItems="center" justify="center">
          <Link
            after={<IconSymbol icon="open_in_new" size="18" />}
            css={virtualCareProviderCardLinkCss(mobileScreen || mapView)}
            data-auto-testid={`virtual-care-provider-link`}
            data-testid={`virtual-care-provider-link`}
            onClick={handleGetCareClick}
          >
            {t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE')}
          </Link>
        </Flex>
      </Flex>
    </V2Card>
  );
};
